<template>
  <div class="workbench">
    <div class="stat-cards">
      <div class="stat-card stat-card-blue">
        <div class="stat-icon">
          <video-camera-outlined />
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.generatedVideos }}</div>
          <div class="stat-label">已生成视频</div>
        </div>
      </div>
      <div class="stat-card stat-card-green">
        <div class="stat-icon">
          <loading-outlined />
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.generatingTasks }}</div>
          <div class="stat-label">正在生成任务</div>
        </div>
      </div>
      <div class="stat-card stat-card-yellow">
        <div class="stat-icon">
          <clock-circle-outlined />
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.pendingTasks }}</div>
          <div class="stat-label">待处理任务</div>
        </div>
      </div>
      <div class="stat-card stat-card-red">
        <div class="stat-icon">
          <exclamation-circle-outlined />
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.errorTasks }}</div>
          <div class="stat-label">异常任务</div>
        </div>
      </div>
    </div>

    <div class="content-row">
      <div class="recent-tasks">
        <div class="section-header">
          <span class="section-title">最近任务</span>
          <a class="view-all" @click="handleViewAll">查看全部</a>
        </div>
        <div v-if="tasks.length === 0" class="empty-state">
          <div class="empty-title">暂无混剪任务</div>
          <div class="empty-desc">请先在镜头剪辑中配置模板与素材，创建任务后到任务中心合成。</div>
          <a-button type="primary" class="goto-btn" @click="goToShotEditing">去镜头剪辑</a-button>
        </div>
        <div v-else class="task-list">
          <div class="task-item" v-for="task in tasks.slice(0, 5)" :key="task.id">
            <div class="task-item-header">
              <span class="task-name">{{ task.name }}</span>
              <a-tag :color="taskStatusColor(task.status)">{{ taskStatusText(task.status) }}</a-tag>
            </div>
            <div class="task-item-meta">
              <span>{{ task.template }}</span>
              <span>{{ task.createdAt }}</span>
            </div>
            <div class="task-item-actions" v-if="task.status === 'pending'">
              <a-button type="primary" size="small" class="btn-start" :loading="task.executing" @click="handleStartTask(task)">
                <play-circle-outlined /> 开始执行
              </a-button>
            </div>
            <div class="task-item-actions" v-if="task.status === 'generating'">
              <a-button type="default" size="small" disabled>
                <loading-outlined /> 生成中...
              </a-button>
            </div>
            <div class="task-item-actions" v-if="task.status === 'completed'">
              <a-button type="primary" size="small" class="btn-start" @click="handleStartTask(task)">
                <play-circle-outlined /> 重新执行
              </a-button>
              <a-button v-if="task.video_url" type="primary" size="small" class="btn-download" @click="handleDownloadVideo(task)">
                <download-outlined /> 下载视频
              </a-button>
            </div>
            <div class="task-item-actions" v-if="task.status === 'error'">
              <a-button type="primary" size="small" class="btn-start" @click="handleStartTask(task)">
                <reload-outlined /> 重新执行
              </a-button>
            </div>
          </div>
        </div>
      </div>

      <div class="right-panel">
        <div class="quick-entry">
          <div class="section-title">快捷入口</div>
          <div class="entry-grid">
            <div class="entry-item" @click="goToMaterialCenter">
              <div class="entry-icon entry-icon-orange">
                <folder-outlined />
              </div>
              <span class="entry-label">素材中心</span>
            </div>
            <div class="entry-item" @click="goToShotEditing">
              <div class="entry-icon entry-icon-blue">
                <video-camera-outlined />
              </div>
              <span class="entry-label">镜头剪辑</span>
            </div>
            <div class="entry-item" @click="handleComingSoon('任务中心')">
              <div class="entry-icon entry-icon-green">
                <play-circle-outlined />
              </div>
              <span class="entry-label">任务中心</span>
            </div>
            <div class="entry-item" @click="handleComingSoon('发布排期')">
              <div class="entry-icon entry-icon-pink">
                <schedule-outlined />
              </div>
              <span class="entry-label">发布排期</span>
            </div>
            <div class="entry-item" @click="handleComingSoon('系统设置')">
              <div class="entry-icon entry-icon-gray">
                <setting-outlined />
              </div>
              <span class="entry-label">系统设置</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import {
  VideoCameraOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FolderOutlined,
  PlayCircleOutlined,
  ScheduleOutlined,
  SettingOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { getTaskList, composeVideo, updateTaskStatus } from '@/api/videoEdit';
import { getFileFromDB, getAllFilesFromFolder } from '@/utils/indexedDB';

const navigateTo = inject('navigateTo', () => {});

const tasks = ref([]);

const stats = computed(() => ({
  generatedVideos: tasks.value.filter(t => t.status === 'completed').length,
  generatingTasks: tasks.value.filter(t => t.status === 'generating').length,
  pendingTasks: tasks.value.filter(t => t.status === 'pending').length,
  errorTasks: tasks.value.filter(t => t.status === 'error').length,
}));

onMounted(() => {
  loadTasks();
});

async function loadTasks() {
  try {
    const list = await getTaskList();
    tasks.value = list.map(t => {
      const createdAt = t.createdAt || t.created_at;
      const templateName = t.taskName || t.task_name || '';
      let formattedCreatedAt = '';
      if (createdAt) {
        const d = new Date(createdAt);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        const s = String(d.getSeconds()).padStart(2, '0');
        formattedCreatedAt = `${y}/${m}/${day} ${h}:${min}:${s}`;
      }
      return {
        id: t.id,
        name: templateName,
        template: t.templateId || t.template_id || '',
        status: t.status,
        video_url: t.videoUrl || t.video_url,
        shots: t.shots,
        bgm_file_key: t.bgmFileKey || t.bgm_file_key,
        cover_file_key: t.coverFileKey || t.cover_file_key,
        createdAt: formattedCreatedAt,
      };
    });
  } catch (e) {
    console.warn('Failed to load tasks:', e);
  }
}

function goToMaterialCenter() {
  navigateTo('6');
}

function goToShotEditing() {
  navigateTo('7');
}

function handleViewAll() {
  if (tasks.value.length === 0) {
    message.info('暂无任务，请先去镜头剪辑创建任务');
    return;
  }
  message.info('任务中心功能开发中');
}

function handleComingSoon(name) {
  message.info(`${name} 功能开发中，敬请期待`);
}

function taskStatusColor(status) {
  const map = { pending: 'default', generating: 'processing', completed: 'success', error: 'error' };
  return map[status] || 'default';
}

function taskStatusText(status) {
  const map = { pending: '待处理', generating: '生成中', completed: '已完成', error: '异常' };
  return map[status] || status;
}

async function handleStartTask(task) {
  try {
    task.executing = true;
    
    // Update status to generating - use taskName instead of id
    await updateTaskStatus(task.name, 'generating', null, null);
    task.status = 'generating';
    message.info('任务开始执行...');
    
    // Parse shots
    const shots = typeof task.shots === 'string' ? JSON.parse(task.shots) : task.shots;
    
    if (!shots || !Array.isArray(shots)) {
      throw new Error('任务镜头数据格式错误');
    }
    
    console.log('[Task] Starting task execution:', task);
    console.log('[Task] Shots:', shots);
    
    // Build form data
    const formData = new FormData();
    
    // Add BGM file
    if (task.bgm_file_key) {
      console.log('[Task] Loading BGM file:', task.bgm_file_key);
      const bgmFile = await getFileFromDB(task.bgm_file_key);
      if (bgmFile) {
        formData.append('bgm', bgmFile);
        console.log('[Task] BGM file loaded:', bgmFile.name, bgmFile.size, 'bytes');
      } else {
        console.warn('[Task] BGM file not found in IndexedDB');
      }
    }
    
    // Add cover image
    if (task.cover_file_key) {
      console.log('[Task] Loading cover image:', task.cover_file_key);
      const coverFile = await getFileFromDB(task.cover_file_key);
      if (coverFile) {
        formData.append('coverImage', coverFile);
        console.log('[Task] Cover image loaded:', coverFile.name, coverFile.size, 'bytes');
      } else {
        console.warn('[Task] Cover image not found in IndexedDB');
      }
    }
    
    // Add shot count
    formData.append('shotCount', shots.length);
    
    // Add media files and counts
    const mediaCounts = [];
    const allMediaFiles = [];
    const keepOriginalAudios = [];
    const audioFilesList = [];
    
    for (const shot of shots) {
      console.log('[Task] Processing shot:', shot);
      if (shot.folder_key) {
        // Get all files from this folder
        const files = await getAllFilesFromFolder(shot.folder_key);
        // Only video/image files go to mediaFiles
        const mediaFiles = files.filter(f => {
          const ext = '.' + f.name.split('.').pop().toLowerCase();
          return ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.mpg', '.mpeg', '.3gp', '.ts', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico'].includes(ext);
        });
        console.log(`[Task] Found ${files.length} files in folder "${shot.folder_key}", ${mediaFiles.length} media files`);
        mediaCounts.push(mediaFiles.length);
        allMediaFiles.push(...mediaFiles);
        // Add keepOriginalAudio setting for this shot
        keepOriginalAudios.push(shot.keepOriginalAudio !== false);
      } else {
        console.warn('[Task] Shot has no folder_key');
        mediaCounts.push(0);
        keepOriginalAudios.push(true);
      }
      
      // Add audio file for this shot
      if (shot.audios) {
        const audioFile = await getFileFromDB(shot.audios);
        if (audioFile) {
          audioFilesList.push(audioFile);
          console.log('[Task] Audio file loaded:', audioFile.name);
        } else {
          audioFilesList.push(null);
          console.warn('[Task] Audio file not found:', shot.audios);
        }
      } else {
        audioFilesList.push(null);
      }
    }
    
    formData.append('mediaCounts', mediaCounts.map(String));
    formData.append('keepOriginalAudios', keepOriginalAudios.map(v => String(v)));
    console.log('[Task] Media counts:', mediaCounts);
    console.log('[Task] Keep original audios:', keepOriginalAudios);
    console.log('[Task] Total media files:', allMediaFiles.length);
    
    // Add all media files (video/image only)
    for (const file of allMediaFiles) {
      if (file && file.name) {
        formData.append('mediaFiles', file);
        console.log('[Task] Appending media file:', file.name, file.size, 'bytes');
      } else {
        console.warn('[Task] Invalid file object:', file);
      }
    }
    
    // Add audio files per shot (only shots that have audio selected)
    for (const audioFile of audioFilesList) {
      if (audioFile && audioFile.name) {
        formData.append('audios', audioFile);
        console.log('[Task] Appending audio file:', audioFile.name, audioFile.size, 'bytes');
      }
    }
    
    // Call compose API
    console.log('[Task] Sending request to /video/compose...');
    const result = await composeVideo(formData);
    console.log('[Task] Compose result:', result);
    
    // Update task with video URL - use taskName instead of id
    await updateTaskStatus(task.name, 'completed', result.videoUrl, null);
    
    // Reload tasks to get fresh state from backend
    await loadTasks();
    
    message.success('视频合成成功！');
  } catch (e) {
    console.error('[Task] Execution failed:', e);
    await updateTaskStatus(task.name, 'error', null, e.message);
    
    // Reload tasks to get fresh state from backend
    await loadTasks();
    
    message.error('视频合成失败：' + e.message);
  } finally {
    task.executing = false;
  }
}

function handleDownloadVideo(task) {
  if (task.video_url) {
    const link = document.createElement('a');
    link.href = task.video_url;
    link.download = `${task.name}.mp4`;
    link.click();
    message.success('开始下载视频');
  } else {
    message.warning('视频地址不存在');
  }
}
</script>

<style scoped>
.workbench {
  min-height: 100%;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.stat-card-blue {
  background: #f0f5ff;
}

.stat-card-green {
  background: #f0faf0;
}

.stat-card-yellow {
  background: #fffbe6;
}

.stat-card-red {
  background: #fff1f0;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.stat-card-blue .stat-icon {
  background: #e6f0ff;
  color: #4096ff;
}

.stat-card-green .stat-icon {
  background: #d9f7d9;
  color: #52c41a;
}

.stat-card-yellow .stat-icon {
  background: #fff1b8;
  color: #faad14;
}

.stat-card-red .stat-icon {
  background: #ffccc7;
  color: #ff4d4f;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: #8c8c8c;
  margin-top: 4px;
}

.content-row {
  display: flex;
  gap: 20px;
  margin-top: 20px;
}

.recent-tasks {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

.view-all {
  color: #4096ff;
  font-size: 14px;
  cursor: pointer;
}

.view-all:hover {
  color: #1677ff;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.empty-desc {
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 24px;
  line-height: 1.6;
}

.goto-btn {
  border-radius: 20px;
  padding: 0 24px;
  height: 36px;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-item {
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  transition: all 0.2s;
}

.task-item:hover {
  border-color: #4096ff;
  background: #f0f5ff;
}

.task-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.task-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.task-item-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #8c8c8c;
}

.task-item-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.btn-start {
  border-radius: 6px;
  font-size: 12px;
}

.btn-download {
  border-radius: 6px;
  font-size: 12px;
}

.right-panel {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.quick-entry {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.quick-entry .section-title {
  margin-bottom: 16px;
}

.entry-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.entry-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 8px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.entry-item:hover {
  border-color: #4096ff;
  background: #f0f5ff;
}

.entry-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.entry-icon-orange {
  background: #fff7e6;
  color: #fa8c16;
}

.entry-icon-blue {
  background: #e6f4ff;
  color: #1677ff;
}

.entry-icon-green {
  background: #f6ffed;
  color: #52c41a;
}

.entry-icon-pink {
  background: #fff0f6;
  color: #eb2f96;
}

.entry-icon-gray {
  background: #f5f5f5;
  color: #8c8c8c;
}

.entry-label {
  font-size: 12px;
  color: #595959;
  text-align: center;
}
</style>
